import type { TaskStatus } from "@erp/shared-types";
import { isTerminalTaskStatus } from "@erp/shared-types";

/**
 * Derived checklist phase for a typed task, rolled up from the states of its
 * subtasks against the type's `expectedSubtaskTypeKeys`. Pure and stateless —
 * nothing is persisted; callers recompute on read.
 */

/** The minimal subtask shape the rollup needs. */
export interface TaskPhaseSubtask {
  /** The subtask's task-type key, if it is typed. */
  typeKey?: string | null;
  status: TaskStatus;
}

export type TaskChecklistItemState = "missing" | "open" | "closed";

export interface TaskChecklistItem {
  typeKey: string;
  state: TaskChecklistItemState;
}

export type TaskPhase =
  "no_checklist" | "not_started" | "in_progress" | "complete";

export interface TaskPhaseRollup {
  phase: TaskPhase;
  /** One entry per expected subtask type key, in declaration order. */
  items: TaskChecklistItem[];
  total: number;
  closed: number;
  open: number;
  missing: number;
  /** True when every expected subtask exists and has reached a terminal status. */
  isComplete: boolean;
}

function checklistItemState(
  matching: readonly TaskPhaseSubtask[],
): TaskChecklistItemState {
  if (matching.length === 0) return "missing";
  // Any live subtask of this key keeps the item open; the item only closes
  // once every subtask of the key has reached a terminal status.
  return matching.every((s) => isTerminalTaskStatus(s.status))
    ? "closed"
    : "open";
}

/**
 * Roll the states of `subtasks` up into a checklist phase against
 * `expectedSubtaskTypeKeys`. Subtasks whose type key is not expected (or that
 * are untyped) don't affect the rollup — the checklist only tracks the
 * declared expectations.
 */
export function rollupTaskPhase(
  expectedSubtaskTypeKeys: readonly string[] | null | undefined,
  subtasks: readonly TaskPhaseSubtask[],
): TaskPhaseRollup {
  const expected = expectedSubtaskTypeKeys ?? [];

  const items: TaskChecklistItem[] = expected.map((typeKey) => ({
    typeKey,
    state: checklistItemState(subtasks.filter((s) => s.typeKey === typeKey)),
  }));

  const closed = items.filter((i) => i.state === "closed").length;
  const open = items.filter((i) => i.state === "open").length;
  const missing = items.filter((i) => i.state === "missing").length;
  const isComplete = items.length > 0 && closed === items.length;

  let phase: TaskPhase;
  if (items.length === 0) {
    phase = "no_checklist";
  } else if (isComplete) {
    phase = "complete";
  } else if (missing === items.length) {
    phase = "not_started";
  } else {
    phase = "in_progress";
  }

  return {
    phase,
    items,
    total: items.length,
    closed,
    open,
    missing,
    isComplete,
  };
}
